import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { API, AuthContext } from '@/App';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import { BarChart, TrendingUp, Eye, MousePointerClick, Package, DollarSign, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import Footer from '@/components/Footer';

const PartnerPortal = () => {
  const { user, token } = React.useContext(AuthContext);
  const [analytics, setAnalytics] = useState(null);
  const [partnerships, setPartnerships] = useState([]);
  const [advertisements, setAdvertisements] = useState([]);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (user?.role !== 'partner' && user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchPartnerData();
  }, [user]);

  const fetchPartnerData = async () => {
    try {
      const [analyticsRes, partnershipsRes, adsRes] = await Promise.all([
        axios.get(`${API}/partner/analytics`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/partnerships/my`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/advertisements/my`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setAnalytics(analyticsRes.data);
      setPartnerships(partnershipsRes.data);
      setAdvertisements(adsRes.data);
    } catch (error) {
      console.error('Failed to fetch partner data:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'text-green-700 bg-green-100',
      pending: 'text-yellow-700 bg-yellow-100',
      pending_payment: 'text-orange-700 bg-orange-100',
      expired: 'text-gray-700 bg-gray-100',
      rejected: 'text-red-700 bg-red-100'
    };
    return colors[status] || 'text-gray-700 bg-gray-100';
  };

  const deletePartnership = async (partnershipId) => {
    if (!window.confirm('Are you sure you want to delete this partnership? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/partnerships/${partnershipId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Partnership deleted successfully' });
      fetchPartnerData(); // Refresh the data
    } catch (error) {
      console.error('Failed to delete partnership:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to delete partnership',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #FFE5EC 0%, #FFF8F3 100%)' }}>
      <Navigation />
      <div className="container mx-auto px-4 py-8" data-testid="partner-portal">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
            Partner Portal
          </h1>
          <p className="text-gray-600">Welcome, {user?.full_name}</p>
        </div>

        {analytics && (
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <Card className="glass">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Active Partnerships</CardTitle>
                <Package className="w-5 h-5 text-rose-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {analytics.active_partnerships}
                </div>
              </CardContent>
            </Card>

            <Card className="glass">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Active Ads</CardTitle>
                <TrendingUp className="w-5 h-5 text-rose-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {analytics.active_ads}
                </div>
              </CardContent>
            </Card>

            <Card className="glass">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Total Views</CardTitle>
                <Eye className="w-5 h-5 text-rose-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {analytics.total_views}
                </div>
              </CardContent>
            </Card>

            <Card className="glass">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Total Clicks</CardTitle>
                <MousePointerClick className="w-5 h-5 text-rose-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {analytics.total_clicks}
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  CTR: {analytics.click_through_rate.toFixed(2)}%
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs defaultValue="partnerships" className="space-y-6">
          <TabsList className="glass">
            <TabsTrigger value="partnerships">Partnerships</TabsTrigger>
            <TabsTrigger value="advertisements">Advertisements</TabsTrigger>
            <TabsTrigger value="purchase">Purchase New</TabsTrigger>
          </TabsList>

          <TabsContent value="partnerships">
            <Card className="glass">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>My Partnerships</CardTitle>
                <CardDescription>View and manage your partnership packages</CardDescription>
              </CardHeader>
              <CardContent>
                {partnerships.length > 0 ? (
                  <div className="space-y-4">
                    {partnerships.map((partnership) => (
                      <div key={partnership.id} className="p-4 rounded-lg bg-white/50 border">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h3 className="font-semibold capitalize">{partnership.package_type.replace('_', ' ')}</h3>
                            <p className="text-sm text-gray-600">
                              €{partnership.amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} - {partnership.payment_method}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-3 py-1 rounded-full ${getStatusColor(partnership.status)}`}>
                              {partnership.status.replace('_', ' ')}
                            </span>
                            <button
                              onClick={() => deletePartnership(partnership.id)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Delete partnership"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        {partnership.start_date && partnership.end_date && (
                          <p className="text-sm text-gray-600">
                            {new Date(partnership.start_date).toLocaleDateString()} - {new Date(partnership.end_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No partnerships yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="advertisements">
            <Card className="glass">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>My Advertisements</CardTitle>
                <CardDescription>Track your advertising campaigns</CardDescription>
              </CardHeader>
              <CardContent>
                {advertisements.length > 0 ? (
                  <div className="space-y-4">
                    {advertisements.map((ad) => (
                      <div key={ad.id} className="p-4 rounded-lg bg-white/50 border">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h3 className="font-semibold">{ad.title}</h3>
                            <p className="text-sm text-gray-600 capitalize">{ad.ad_type.replace('_', ' ')}</p>
                          </div>
                          <span className={`text-xs px-3 py-1 rounded-full ${getStatusColor(ad.status)}`}>
                            {ad.status.replace('_', ' ')}
                          </span>
                        </div>
                        <div className="flex gap-4 text-sm text-gray-600 mt-2">
                          <span>👁️ {ad.views} views</span>
                          <span>🖱️ {ad.clicks} clicks</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No advertisements yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="purchase">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="glass">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Partnership Packages</CardTitle>
                  <CardDescription>Long-term visibility and collaboration</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button onClick={() => navigate('/become-partner')} className="w-full bg-rose-500 hover:bg-rose-600">
                    View Partnership Options
                  </Button>
                </CardContent>
              </Card>

              <Card className="glass">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Advertisement Packages</CardTitle>
                  <CardDescription>Targeted visibility campaigns</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button onClick={() => navigate('/become-partner?tab=advertising')} className="w-full bg-rose-500 hover:bg-rose-600">
                    View Advertising Options
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
};

export default PartnerPortal;